import { createServerFn } from "@tanstack/react-start";
import { and, eq, isNull, ne, or } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import type { Author } from "~/lib/citations/identity-resolution";
import {
  adjudications,
  projectCitations,
  projectScreeningSettings,
  screeningDecisions,
} from "~/lib/db/schema";
import type { ScreeningPhase } from "./dual-screening";

// ============================================================================
// Schemas
// ============================================================================

const phaseEnum = z.enum(["title_abstract", "full_text"]);

const projectIdSchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const citationIdSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const submitAdjudicationSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  resolution: z.enum(["include", "exclude", "send_to_full_text"]),
  exclusionCode: z.string().optional(),
  rationale: z.string().optional(),
  phase: phaseEnum.default("title_abstract"),
});

// ============================================================================
// Types
// ============================================================================

export interface ConflictCitation {
  id: string;
  title: string;
  authors: Author[] | null;
  year: number | null;
  containerTitle: string | null;
  doi: string | null;
  pmid: string | null;
  abstract: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  consensusStatus: string;
}

export interface ConflictDetails {
  citation: ConflictCitation;
  decision1: {
    id: string;
    reviewerId: string;
    reviewerName: string;
    decision: string;
    exclusionCode: string | null;
    exclusionNotes: string | null;
    notes: string | null;
    createdAt: Date;
  };
  decision2: {
    id: string;
    reviewerId: string;
    reviewerName: string;
    decision: string;
    exclusionCode: string | null;
    exclusionNotes: string | null;
    notes: string | null;
    createdAt: Date;
  };
  canAdjudicate: boolean;
  adjudicationReason: string | null;
}

// ============================================================================
// Server Functions
// ============================================================================

/**
 * Check if user can adjudicate conflicts for this project
 */
export const $canUserAdjudicate = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    const { role } = await requireProjectAccess(data.projectId, user.id);

    // Get settings
    const settings = await db.query.projectScreeningSettings.findFirst({
      where: eq(projectScreeningSettings.projectId, data.projectId),
    });

    if (!settings) {
      return { canAdjudicate: false, reason: "Screening settings not configured" };
    }

    // Check role eligibility
    const adjudicatorRole = settings.adjudicatorRole;
    if (adjudicatorRole === "owner" && role !== "owner") {
      return { canAdjudicate: false, reason: "Only the project owner can adjudicate" };
    }

    // Check self-adjudication rule (owners can always adjudicate)
    if (!settings.allowSelfAdjudication && role !== "owner") {
      // Get decisions for this citation in this phase
      const decisions = await db.query.screeningDecisions.findMany({
        where: and(
          eq(screeningDecisions.projectCitationId, data.citationId),
          eq(screeningDecisions.phase, data.phase),
          isNull(screeningDecisions.deletedAt),
        ),
      });

      const isInvolvedReviewer = decisions.some((d) => d.reviewerId === user.id);
      if (isInvolvedReviewer) {
        return {
          canAdjudicate: false,
          reason: "You cannot adjudicate conflicts you were involved in",
        };
      }
    }

    return { canAdjudicate: true, reason: null };
  });

/**
 * Submit adjudication for a conflict
 */
export const $submitAdjudication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => submitAdjudicationSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    const { role } = await requireProjectAccess(data.projectId, user.id);

    // Verify citation exists and is in conflict status
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    if (citation.consensusStatus !== "conflict") {
      throw new Error("Citation is not in conflict status");
    }

    // Get settings and verify eligibility
    const settings = await db.query.projectScreeningSettings.findFirst({
      where: eq(projectScreeningSettings.projectId, data.projectId),
    });

    if (!settings) {
      throw new Error("Screening settings not configured");
    }

    if (settings.adjudicatorRole === "owner" && role !== "owner") {
      throw new Error("Only the project owner can adjudicate");
    }

    // Get the two decisions for this phase
    const decisions = await db.query.screeningDecisions.findMany({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.phase, data.phase),
        isNull(screeningDecisions.deletedAt),
      ),
      orderBy: (d, { asc }) => [asc(d.decisionOrder)],
    });

    if (decisions.length < 2) {
      throw new Error("Cannot adjudicate without two decisions");
    }

    // Check self-adjudication (owners can always adjudicate)
    if (!settings.allowSelfAdjudication && role !== "owner") {
      const isInvolved = decisions.some((d) => d.reviewerId === user.id);
      if (isInvolved) {
        throw new Error("You cannot adjudicate conflicts you were involved in");
      }
    }

    // Create adjudication record
    await db.insert(adjudications).values({
      id: nanoid(),
      projectCitationId: data.citationId,
      adjudicatorId: user.id,
      resolution: data.resolution,
      phase: data.phase,
      exclusionCode: data.resolution === "exclude" ? data.exclusionCode : null,
      rationale: data.rationale ?? "",
      decision1Id: decisions[0].id,
      decision2Id: decisions[1].id,
    });

    // Map resolution to screening decision
    let finalDecision: "include" | "exclude" | "maybe";
    let screeningStatus: "included" | "excluded" | "maybe";

    if (data.resolution === "include" || data.resolution === "send_to_full_text") {
      finalDecision = "include";
      screeningStatus = "included";
    } else {
      finalDecision = "exclude";
      screeningStatus = "excluded";
    }

    // Update citation status
    await db
      .update(projectCitations)
      .set({
        consensusStatus: "adjudicated",
        finalDecision,
        finalDecisionAt: new Date(),
        finalDecisionBy: user.id,
        screeningStatus,
        screenedBy: user.id,
        screenedAt: new Date(),
        exclusionCode: data.resolution === "exclude" ? data.exclusionCode : null,
      })
      .where(eq(projectCitations.id, data.citationId));

    return { success: true };
  });

/**
 * Undo an adjudication — delete the adjudication record and reset citation to conflict
 */
export const $undoAdjudication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Find the most recent adjudication for this citation/phase by the current user
    const adjudication = await db.query.adjudications.findFirst({
      where: and(
        eq(adjudications.projectCitationId, data.citationId),
        eq(adjudications.adjudicatorId, user.id),
        eq(adjudications.phase, data.phase),
      ),
      orderBy: (a, { desc }) => [desc(a.createdAt)],
    });

    if (!adjudication) {
      throw new Error("No adjudication found to undo");
    }

    // Delete the adjudication record
    await db.delete(adjudications).where(eq(adjudications.id, adjudication.id));

    // Reset citation back to conflict status
    await db
      .update(projectCitations)
      .set({
        consensusStatus: "conflict",
        finalDecision: null,
        finalDecisionAt: null,
        finalDecisionBy: null,
        screeningStatus: "pending",
        screenedBy: null,
        screenedAt: null,
        exclusionCode: null,
      })
      .where(eq(projectCitations.id, data.citationId));

    return { success: true };
  });

/**
 * Get adjudication queue (citations in conflict status)
 */
export const $getAdjudicationQueue = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        eq(projectCitations.consensusStatus, "conflict"),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      with: {
        canonicalReference: true,
      },
      orderBy: (citations, { asc }) => [asc(citations.updatedAt)],
    });

    return citations.map((c) => ({
      id: c.id,
      title: c.canonicalReference.title,
      authors: c.canonicalReference.authors as Author[] | null,
      year: c.canonicalReference.year,
      containerTitle: c.canonicalReference.containerTitle,
      doi: c.canonicalReference.doi,
      pmid: c.canonicalReference.pmid,
      abstract: c.canonicalReference.abstract,
      volume: c.canonicalReference.volume,
      issue: c.canonicalReference.issue,
      pages: c.canonicalReference.pages,
      consensusStatus: c.consensusStatus ?? "conflict",
    })) satisfies ConflictCitation[];
  });

/**
 * Get detailed conflict information for a citation
 */
export const $getConflictDetails = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    const { role } = await requireProjectAccess(data.projectId, user.id);

    // Get citation
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
      with: {
        canonicalReference: true,
      },
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    // Get decisions with reviewer info (for this phase)
    const decisions = await db.query.screeningDecisions.findMany({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.phase, data.phase),
        isNull(screeningDecisions.deletedAt),
      ),
      with: {
        reviewer: true,
      },
      orderBy: (d, { asc }) => [asc(d.decisionOrder)],
    });

    if (decisions.length < 2) {
      throw new Error("Conflict details require at least two decisions");
    }

    // Check adjudication eligibility
    const settings = await db.query.projectScreeningSettings.findFirst({
      where: eq(projectScreeningSettings.projectId, data.projectId),
    });

    let canAdjudicate = true;
    let adjudicationReason: string | null = null;

    if (!settings) {
      canAdjudicate = false;
      adjudicationReason = "Screening settings not configured";
    } else {
      if (settings.adjudicatorRole === "owner" && role !== "owner") {
        canAdjudicate = false;
        adjudicationReason = "Only the project owner can adjudicate";
      } else if (!settings.allowSelfAdjudication && role !== "owner") {
        const isInvolved = decisions.some((d) => d.reviewerId === user.id);
        if (isInvolved) {
          canAdjudicate = false;
          adjudicationReason = "You cannot adjudicate conflicts you were involved in";
        }
      }
    }

    return {
      citation: {
        id: citation.id,
        title: citation.canonicalReference.title,
        authors: citation.canonicalReference.authors as Author[] | null,
        year: citation.canonicalReference.year,
        containerTitle: citation.canonicalReference.containerTitle,
        doi: citation.canonicalReference.doi,
        pmid: citation.canonicalReference.pmid,
        abstract: citation.canonicalReference.abstract,
        volume: citation.canonicalReference.volume,
        issue: citation.canonicalReference.issue,
        pages: citation.canonicalReference.pages,
        consensusStatus: citation.consensusStatus ?? "conflict",
      },
      decision1: {
        id: decisions[0].id,
        reviewerId: decisions[0].reviewerId,
        reviewerName: decisions[0].reviewer.name,
        decision: decisions[0].decision,
        exclusionCode: decisions[0].exclusionCode,
        exclusionNotes: decisions[0].exclusionNotes,
        notes: decisions[0].notes,
        createdAt: decisions[0].createdAt,
      },
      decision2: {
        id: decisions[1].id,
        reviewerId: decisions[1].reviewerId,
        reviewerName: decisions[1].reviewer.name,
        decision: decisions[1].decision,
        exclusionCode: decisions[1].exclusionCode,
        exclusionNotes: decisions[1].exclusionNotes,
        notes: decisions[1].notes,
        createdAt: decisions[1].createdAt,
      },
      canAdjudicate,
      adjudicationReason,
    } satisfies ConflictDetails;
  });

/**
 * Get adjudication history for a citation
 */
export const $getAdjudicationHistory = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const adjudicationRecords = await db.query.adjudications.findMany({
      where: eq(adjudications.projectCitationId, data.citationId),
      with: {
        adjudicator: true,
        decision1: {
          with: {
            reviewer: true,
          },
        },
        decision2: {
          with: {
            reviewer: true,
          },
        },
      },
      orderBy: (a, { desc }) => [desc(a.createdAt)],
    });

    return adjudicationRecords.map((a) => ({
      id: a.id,
      adjudicatorName: a.adjudicator.name,
      resolution: a.resolution,
      exclusionCode: a.exclusionCode,
      rationale: a.rationale,
      createdAt: a.createdAt,
      decision1: {
        reviewerName: a.decision1.reviewer.name,
        decision: a.decision1.decision,
      },
      decision2: {
        reviewerName: a.decision2.reviewer.name,
        decision: a.decision2.decision,
      },
    }));
  });

/**
 * Get conflict counts per phase (for sidebar badges)
 */
export const $getConflictCounts = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ projectId: z.string() }).parse(data),
  )
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const conflicts = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.consensusStatus, "conflict"),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      columns: {
        screeningPhase: true,
      },
    });

    let titleAbstract = 0;
    let fullText = 0;
    for (const c of conflicts) {
      if (c.screeningPhase === "full_text") {
        fullText++;
      } else {
        titleAbstract++;
      }
    }

    return { titleAbstract, fullText };
  });
